/**
 * Demo Layout Component
 * 
 * Main layout component for the interactive demo experience.
 * Integrates with the existing ChatOperationalLayout while adding demo-specific features.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChatOperationalLayout } from '../ChatOperational/ChatOperationalLayout';
import { ContextualOverlay } from './ContextualOverlay';
import { DemoDevTools } from './DemoDevTools';
import { DemoContext } from '../../contexts/DemoContext';
import { useDemo } from '../../hooks/useDemo';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useChat } from '../../contexts/ChatContext';
import './DemoLayout.css';

interface DemoLayoutProps {
  onToggleUI?: () => void;
  showUIToggle?: boolean;
  currentDemoUser?: {
    id: string;
    full_name?: string;
    name?: string;
    role: string;
  } | null;
  availableDemoUsers?: Array<{
    id: string;
    full_name?: string;
    name?: string;
    role: string;
  }>;
  onUserSelect?: (userId: string) => Promise<void>;
}

export const DemoLayout: React.FC<DemoLayoutProps> = ({
  onToggleUI,
  showUIToggle = true,
  currentDemoUser,
  availableDemoUsers = [],
  onUserSelect
}) => {
  const {
    currentSession,
    currentScene,
    sceneData,
    isLoading,
    error,
    processUserInput,
    advanceScene,
    handleCardAction
  } = useDemo();

  const { setDataSource } = useModelSelector();
  const { loadUserThreads, selectThread } = useChat();

  const [isDemoActive, setIsDemoActive] = useState(true);
  const [showHelp, setShowHelp] = useState(true);
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);
  const [hasShownOverlays, setHasShownOverlays] = useState({
    chatInput: false,
    artifactCard: false,
    contextSection: false,
    demoControls: false
  });

  // Fallback demo users if not provided
  const fallbackUsers = [
    { id: 'demo-user-1', full_name: 'Demo User', role: 'Business Owner' },
    { id: 'demo-user-2', full_name: 'Alex Johnson', role: 'Operations Manager' },
    { id: 'demo-user-3', full_name: 'Sarah Chen', role: 'Financial Analyst' }
  ];

  const effectiveUsers = availableDemoUsers.length > 0 
    ? availableDemoUsers.map(user => ({
        id: user.id,
        full_name: user.full_name || user.name || 'Unknown User',
        role: user.role
      }))
    : fallbackUsers;
  const effectiveCurrentUser = currentDemoUser 
    ? {
        id: currentDemoUser.id,
        full_name: currentDemoUser.full_name || currentDemoUser.name || 'Unknown User',
        role: currentDemoUser.role
      }
    : fallbackUsers[0];

  // Initialize demo mode - keep live data mode by default
  useEffect(() => {
    console.log('Initializing demo mode with live data');
    // Don't automatically switch to sample mode - let user stay in live mode
  }, []);

  // Initialize demo session when component mounts
  useEffect(() => {
    if (!currentSession && !isLoading) {
      // Demo session will be created by the useDemo hook
      console.log('Demo layout initialized');
    }
  }, [currentSession, isLoading]);

  // Show contextual overlays based on scene progression
  useEffect(() => {
    if (currentSession && currentScene >= 0) {
      const timer = setTimeout(() => {
        if (currentScene === 0 && !hasShownOverlays.demoControls) {
          setActiveOverlay('demo-controls');
          setHasShownOverlays(prev => ({ ...prev, demoControls: true }));
        } else if (currentScene === 1 && !hasShownOverlays.chatInput) {
          setActiveOverlay('chat-input');
          setHasShownOverlays(prev => ({ ...prev, chatInput: true }));
        } else if (currentScene >= 2 && !hasShownOverlays.artifactCard) {
          setActiveOverlay('artifact-card');
          setHasShownOverlays(prev => ({ ...prev, artifactCard: true }));
        }
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [currentSession, currentScene, hasShownOverlays]);

  // Handle demo-specific message processing
  const handleDemoMessage = useCallback(async (message: string) => {
    if (!currentSession) {
      console.error('No demo session available');
      return;
    }

    try {
      await processUserInput(message);
    } catch (error) {
      console.error('Failed to process demo message:', error);
    }
  }, [currentSession, processUserInput]);

  // Handle demo completion
  const handleDemoComplete = useCallback(() => {
    setIsDemoActive(false);
    console.log('Demo completed successfully');
  }, []);

  // Handle demo navigation
  const handlePreviousScene = useCallback(async () => {
    // For now, we don't support going back in the demo
    // This could be implemented if needed
    console.log('Previous scene not supported in demo flow');
  }, []);

  const handleNextScene = useCallback(async () => {
    if (!currentSession) return;

    try {
      await advanceScene();
    } catch (error) {
      console.error('Failed to advance scene:', error);
    }
  }, [currentSession, advanceScene]);

  const handleSkipScene = useCallback(async () => {
    // Skip current scene and advance to next
    await handleNextScene();
  }, [handleNextScene]);

  // Handle demo card actions
  const handleDemoCardAction = useCallback(async (action: string, payload: any) => {
    if (!currentSession) return;

    try {
      // First handle the card action (updates scene progress in backend)
      await handleCardAction(action, payload);
      
      // Check if action should advance scene
      if (action === 'action-completed' && payload?.advanceScene === true) {
        console.log('🎯 [DemoLayout] Action completed with advanceScene flag, advancing to next scene...');
        
        // Delay to allow backend commit and state updates to propagate
        setTimeout(async () => {
          try {
            console.log('🔄 [DemoLayout] Attempting to advance scene...');
            
            // Advance the scene (it will validate completion)
            const result = await advanceScene();
            console.log('✅ [DemoLayout] Successfully advanced to next scene', result);
            
            // Refresh threads and select the new thread
            if (result && (result as any).thread_id) {
              console.log('🔄 [DemoLayout] Refreshing threads and selecting new thread:', (result as any).thread_id);
              
              // Wait a bit for backend to fully commit
              setTimeout(async () => {
                await loadUserThreads();
                await selectThread((result as any).thread_id);
                console.log('✅ [DemoLayout] Navigated to new scene thread');
              }, 300);
            }
          } catch (error) {
            console.error('❌ [DemoLayout] Failed to advance scene:', error);
            // Scene is not yet complete - this is expected if user hasn't filled all required fields
          }
        }, 1000); // Increased delay to ensure backend updates are committed
      }
    } catch (error) {
      console.error('Failed to handle card action:', error);
    }
  }, [currentSession, handleCardAction, advanceScene, loadUserThreads, selectThread]);

  // Handle user selection
  const handleUserSelect = useCallback(async (userId: string) => {
    if (onUserSelect) {
      try {
        await onUserSelect(userId);
      } catch (error) {
        console.error('DemoLayout: Failed to select user:', error);
      }
    } else {
      console.warn('DemoLayout: No onUserSelect handler provided');
    }
  }, [onUserSelect]);


  // Handle overlay close
  const handleCloseOverlay = useCallback(() => {
    setActiveOverlay(null);
  }, []);

  // Handle task window input submission
  const handleTaskInputSubmit = useCallback(async (input: string) => {
    if (!currentSession) return;

    try {
      await processUserInput(input);
      // Task completion is handled by the demo controller
    } catch (error) {
      console.error('Failed to process task input:', error);
    }
  }, [currentSession, processUserInput, sceneData]);

  // Handle next scene from task window
  const handleTaskNextScene = useCallback(async () => {
    try {
      await handleNextScene();
    } catch (error) {
      console.error('Failed to advance scene from task window:', error);
    }
  }, [handleNextScene]);

  // Determine current task based on scene
  const getCurrentTask = useCallback(() => {
    switch (currentScene) {
      case 0:
        return "Explore the demo interface and familiarize yourself with the controls";
      case 1:
        return "Type your company name in the chat to create your business model";
      case 2:
        return "Review the organizational assessment results and understand the scoring";
      case 3:
        return "Analyze the business value metrics and identify improvement opportunities";
      case 4:
        return "Examine the financial flow integration and forecasting capabilities";
      case 5:
        return "Run scenario simulations to see how changes affect your business";
      case 6:
        return "Learn about continuous monitoring and automated problem detection";
      case 7:
        return "Explore system integrations and data translation capabilities";
      case 8:
        return "Review governance features and automated reporting";
      case 9:
        return "Complete the demo and provide your contact information";
      default:
        return "Continue exploring the demo features";
    }
  }, [currentScene]);

  // Get current scene info
  const getCurrentSceneInfo = useCallback(() => {
    if (!sceneData || !currentSession) return null;
    
    const sceneConfig = sceneData[currentScene];
    return {
      title: sceneConfig?.scene_title || `Scene ${currentScene + 1}`,
      description: sceneConfig?.scene_description || 'Demo scene',
      totalScenes: 10 // There are 10 scenes in the demo (0-9)
    };
  }, [sceneData, currentScene, currentSession]);

  // Effect to control task window visibility
  useEffect(() => {
    console.log('DemoLayout: Task window visibility effect triggered', {
      sceneData,
      currentSession,
    });

    if (!sceneData || !currentSession) {
      console.log('DemoLayout: Missing sceneData or currentSession, hiding task window');
      return;
    }

    // The scene data structure has required_inputs and missing_inputs at the root level
    const shouldShow = sceneData.missing_inputs?.length > 0 || sceneData.is_complete;
    
    console.log('DemoLayout: Task window visibility check:', {
      sceneData,
      missingInputs: sceneData.missing_inputs,
      isComplete: sceneData.is_complete,
      shouldShow,
    });
    
    // Force the state update
    
    // Log after state update
    setTimeout(() => {
      console.log('DemoLayout: Task window state updated to:', shouldShow);
    }, 0);
  }, [sceneData, currentSession]);

  // Demo context value
  const demoContextValue = {
    currentSession,
    currentScene,
    sceneData,
    isDemoActive,
    isLoading,
    error,
    processUserInput: handleDemoMessage,
    advanceScene: handleNextScene,
    handleCardAction: handleDemoCardAction,
    onDemoComplete: handleDemoComplete
  };

  if (error) {
    return (
      <div className="demo-error">
        <h2>Demo Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          Restart Demo
        </button>
      </div>
    );
  }

  const sceneInfo = getCurrentSceneInfo();

  return (
    <DemoContext.Provider value={demoContextValue}>
      <div className="demo-layout">
        {/* Main Chat Interface */}
        <div className="demo-chat-container">
          <ChatOperationalLayout
            demoMode={true}
            demoSession={currentSession}
            demoSceneData={sceneData}
            onToggleUI={onToggleUI}
            showUIToggle={showUIToggle}
            onMessage={handleDemoMessage}
            onCardAction={handleDemoCardAction}
            showHeader={false}
          />
        </div>

        {/* Contextual Overlays */}
        <ContextualOverlay
          target="demo-controls"
          isVisible={activeOverlay === 'demo-controls'}
          onClose={handleCloseOverlay}
          position="top"
          title="Demo Controls"
          content="This is the demo control panel in the sidebar. Here you can see your progress, switch between demo users, and access help for each scene. Click on different scenes to explore their help content."
          actionHint="Try clicking on different scenes in the sidebar to see their help content"
        />

        <ContextualOverlay
          target="chat-input"
          isVisible={activeOverlay === 'chat-input'}
          onClose={handleCloseOverlay}
          position="bottom"
          title="Chat Input"
          content="This is where you interact with Krysalis. Type natural language messages or use slash commands. Krysalis understands context and will guide you through the demo experience."
          actionHint="Try typing 'My company is Acme Corp' to see how Krysalis processes your input"
        />

        <ContextualOverlay
          target="artifact-card"
          isVisible={activeOverlay === 'artifact-card'}
          onClose={handleCloseOverlay}
          position="bottom"
          title="Artifact Cards"
          content="These cards represent business entities and their states in your model. Each artifact has properties, states, and can transition based on business processes. This is how Krysalis models your business behavior."
          actionHint="Click on different artifacts to explore their properties and states"
        />

        <ContextualOverlay
          target="context-section"
          isVisible={activeOverlay === 'context-section'}
          onClose={handleCloseOverlay}
          position="right"
          title="Context Panel"
          content="The right panel shows contextual information about selected items. It provides detailed views of artifacts, processes, and business relationships."
          actionHint="Select different items in the interface to see their details here"
        />

        {/* Demo Development Tools */}
        <DemoDevTools
          sessionKey={currentSession?.session_key}
          onRefresh={() => {
            // Refresh the UI after generating scenes
            window.location.reload();
          }}
        />

      </div>
    </DemoContext.Provider>
  );
};

export default DemoLayout;
